// Controls
import { ChoiceGroupOption, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, SpinButton, useTheme } from "@fluentui/react";
import { Dropdown } from "../dropdown/Dropdown";
import { WeekdayPicker } from "../weekdayPicker/WeekdayPicker";
// Constants
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { MaxDayOfMonth, MaxMonth, MinDayOfMonth, MinMonth, MinRecurrenceInterval } from "@ms/uno-constants/lib/local/RecurrenceConstants";
// Models
import { TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
import { RecurrencePattern } from "@ms/uno-models/lib/local/client/legacyPlanner/task/recurrence/RecurrencePattern";
import { RecurrenceSchedule } from "@ms/uno-models/lib/local/client/legacyPlanner/task/recurrence/RecurrenceSchedule";
import { getMomentMonthIndexFromRecurrenceMonth, getRecurrenceIntervalLimits, getRecurrenceMonthIndexFromMoment } from "@ms/uno-models/lib/local/client/legacyPlanner/task/recurrence/utilities/RecurrencePatternUtilities";
import { RecurrenceDropdownType } from "@ms/uno-models/lib/local/service/graph-legacy/task/recurrence/RecurrenceDropdownType";
import { RecurrencePatternType } from "@ms/uno-models/lib/local/service/graph-legacy/task/recurrence/RecurrencePatternType";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { getChoiceGroupOptionStyles, getClassNames, getDialogContentStyles, getDropdownStyles, getSpinButtonArrowStyles, getSpinButtonStyles, getStyles } from "./CustomRecurrenceDialog.styles";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { css } from "@fluentui/utilities/lib/css";
import { RelativeWeekIndex } from "@ms/uno-constants/lib/local/DateConstants";
import { getDayOfWeekFromMomentIndex, getDaysOfWeekByStartDay, getMomentDayIndexFromDayOfWeek, getWeekIndexFromMoment, ToUTC10am } from "@ms/uno-utilities/lib/local/DateUtilities";
import clamp from "lodash/clamp";
import clone from "lodash/clone";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useLoggers } from "../../../hooks/useLoggers";
import { createNullableContext, useNullSafeContext } from "../../../hooks/useNullSafeContext";
const ComponentContext = createNullableContext();
/**
 * The custom recurrence dialog control
 */ export function CustomRecurrenceDialog(props) {
    const theme = useTheme();
    const defaultState = getDefaultState(props);
    const [state, setState] = useState(defaultState);
    const { engagementLogger } = useLoggers();
    const classNames = getClassNames(getStyles(theme, props.contentStyles));
    // Build context
    const context = useMemo(()=>({
            task: props.task,
            classNames,
            state,
            engagementLogger,
            defaultState,
            setState
        }), [
        classNames,
        state,
        engagementLogger,
        defaultState,
        props.task
    ]);
    return /*#__PURE__*/ React.createElement(ComponentContext.Provider, {
        value: context
    }, /*#__PURE__*/ React.createElement(Dialog, {
        hidden: false,
        onDismiss: props.onDismiss,
        dialogContentProps: {
            className: css(props.dialogContentClassName, classNames.dialogContent),
            title: Strings.TaskRecurrence_CustomRecurrenceDialogTitle,
            type: DialogType.close,
            styles: getDialogContentStyles(getStyles(theme, props.contentStyles))
        },
        modalProps: {
            className: props.dialogClassName,
            containerClassName: css(props.dialogContainerClassName, classNames.dialogContainer),
            isBlocking: false
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.body
    }, /*#__PURE__*/ React.createElement(RepeatSection, null), validateRecurrenceParametersState(state) && /*#__PURE__*/ React.createElement("div", {
        className: classNames.summaryText
    }, generateRecurrenceSummaryText(state))), /*#__PURE__*/ React.createElement(DialogFooter, null, /*#__PURE__*/ React.createElement(PrimaryButton, {
        className: css("saveButton", classNames.dialogFooterButtons),
        onClick: useCallback(()=>onClickSaveButton(props, context), [
            props,
            context
        ]),
        disabled: !validateRecurrenceParametersState(state)
    }, Strings.SharedComponents_SaveButtonText), /*#__PURE__*/ React.createElement(DefaultButton, {
        className: css("cancelButton", classNames.dialogFooterButtons),
        onClick: props.onDismiss
    }, Strings.SharedComponents_CancelButtonText)))));
}
/**
 * Repeat section
 */ function RepeatSection() {
    const { task, classNames, state, defaultState, setState } = useNullSafeContext(ComponentContext);
    const { daysOfWeek, interval, recurrenceDropdownType } = state;
    const theme = useTheme();
    const maxIntervalLimit = getRecurrenceIntervalLimits(recurrenceDropdownType);
    const isIntervalSpinButtonDisabled = recurrenceDropdownType === RecurrenceDropdownType.Weekly && daysOfWeek.length > 1;
    const isIntervalIncreaseDisabled = isIntervalSpinButtonDisabled || interval === maxIntervalLimit;
    const isIntervalDecreaseDisabled = isIntervalSpinButtonDisabled || interval === MinRecurrenceInterval;
    const isMonthlyOrYearlyRecurrence = recurrenceDropdownType === RecurrenceDropdownType.Monthly || recurrenceDropdownType === RecurrenceDropdownType.Yearly;
    const isWeeklyRecurrence = recurrenceDropdownType === RecurrenceDropdownType.Weekly;
    const onIncrementIntervalSpinButton = useCallback(()=>{
        setState({
            ...state,
            interval: sanitizeInterval(state.interval + 1, maxIntervalLimit)
        });
    }, [
        maxIntervalLimit,
        setState,
        state
    ]);
    const onDecrementIntervalSpinButton = useCallback(()=>{
        setState({
            ...state,
            interval: sanitizeInterval(state.interval - 1, maxIntervalLimit)
        });
    }, [
        maxIntervalLimit,
        setState,
        state
    ]);
    const onValidateIntervalSpinButton = useCallback((value)=>{
        // Try to convert the input string to a number
        setState({
            ...state,
            interval: sanitizeInterval(parseInt(value, 10), maxIntervalLimit)
        });
    }, [
        maxIntervalLimit,
        setState,
        state
    ]);
    const onChangeRepeatDropdown = useCallback((evt, option)=>setState({
            ...state,
            ...getStateUpdateForNewRecurrenceType(option.key, state, defaultState.daysOfWeek)
        }), [
        defaultState.daysOfWeek,
        setState,
        state
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.repeatSection
    }, /*#__PURE__*/ React.createElement("div", {
        className: "repeatPattern"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "repeatLabel",
        key: "repeatLabel"
    }, Strings.TaskRecurrence_RepeatLabel), /*#__PURE__*/ React.createElement(SpinButton, {
        className: "spinButton",
        value: interval.toString(),
        min: MinRecurrenceInterval,
        max: maxIntervalLimit,
        styles: getSpinButtonStyles(theme, isIntervalSpinButtonDisabled),
        onIncrement: onIncrementIntervalSpinButton,
        onDecrement: onDecrementIntervalSpinButton,
        onValidate: onValidateIntervalSpinButton,
        disabled: isIntervalSpinButtonDisabled,
        upArrowButtonStyles: getSpinButtonArrowStyles(theme, isIntervalIncreaseDisabled),
        downArrowButtonStyles: getSpinButtonArrowStyles(theme, isIntervalDecreaseDisabled),
        inputProps: {
            "aria-label": Strings.TaskRecurrence_IntervalInputAriaLabel
        },
        incrementButtonAriaLabel: Strings.TaskRecurrence_IncrementIntervalButtonAriaLabel,
        decrementButtonAriaLabel: Strings.TaskRecurrence_DecrementIntervalButtonAriaLabel
    }), /*#__PURE__*/ React.createElement(Dropdown, {
        className: "dropdown",
        key: "recurrenceDropdown",
        options: useMemo(()=>getRecurrenceDropdownTypeOptions(), []),
        onChange: onChangeRepeatDropdown,
        selectedKey: recurrenceDropdownType,
        ariaLabel: Strings.TaskRecurrence_RepeatPatternDropdownAriaLabel,
        styles: getDropdownStyles(theme, false)
    })), isWeeklyRecurrence && /*#__PURE__*/ React.createElement(WeekPicker, null), isMonthlyOrYearlyRecurrence && /*#__PURE__*/ React.createElement("div", {
        className: classNames.monthAndYearlyPickerSection,
        role: "radiogroup"
    }, /*#__PURE__*/ React.createElement(AbsoluteRow, null), task.kind !== TaskKind.Todo && /*#__PURE__*/ React.createElement(RelativeRow, null)));
}
/**
 * Week Picker
 */ function WeekPicker() {
    const { classNames, state, setState } = useNullSafeContext(ComponentContext);
    const { daysOfWeek } = state;
    /**
     * Callback to set the DayOfWeek of the recurrence pattern
     * @param day The day selected
     * @param setDay Whether to set or unset the day in the recurrence. Value of true will add the day, false will remove the day if already set.
     */ const setDayOfWeekPattern = useCallback((day, setDay)=>{
        const newDaysOfWeek = clone(state.daysOfWeek);
        const dayIndex = newDaysOfWeek.indexOf(day);
        let newInterval = state.interval;
        if (dayIndex > -1 && !setDay) {
            // Day exists, so remove it
            newDaysOfWeek.splice(dayIndex, 1);
        } else if (dayIndex < 0 && setDay) {
            // Day does not exist in pattern yet, so add it
            newDaysOfWeek.push(day);
        }
        // If multiple days are selected in weekly pattern type, interval must be 1
        if (state.recurrenceDropdownType === RecurrenceDropdownType.Weekly && newDaysOfWeek.length > 1 && newInterval > 1) {
            newInterval = 1;
        }
        setState({
            ...state,
            daysOfWeek: newDaysOfWeek,
            interval: newInterval
        });
    }, [
        setState,
        state
    ]);
    const errorMessage = validateRecurrenceParametersState(state) ? null : Strings.TaskRecurrence_WeekdayErrorMessage;
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.weekdayPickerSection
    }, /*#__PURE__*/ React.createElement("div", {
        className: "weekdayPickerRow"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "weekdayPickerLabel",
        key: "weekdayPickerLabel"
    }, Strings.TaskRecurrence_WeekdayPickerLabel), /*#__PURE__*/ React.createElement(WeekdayPicker, {
        selectedDaysOfWeek: daysOfWeek,
        onClickWeekday: setDayOfWeekPattern
    })), errorMessage && /*#__PURE__*/ React.createElement("div", {
        className: classNames.errorMessage
    }, errorMessage));
}
/**
 * Absolute recurrence fields row
 */ function AbsoluteRow() {
    const { state, setState } = useNullSafeContext(ComponentContext);
    const { dayOfMonth, isAbsolute, month, recurrenceDropdownType } = state;
    const theme = useTheme();
    const isMonthly = recurrenceDropdownType === RecurrenceDropdownType.Monthly;
    const isYearly = recurrenceDropdownType === RecurrenceDropdownType.Yearly;
    const isAbsoluteDisabled = !isAbsolute || !validateRecurrenceParametersState(state);
    const spinButtonDisabled = isAbsoluteDisabled;
    const onIncrementDayOfMonthSpinButton = useCallback(()=>{
        setState({
            ...state,
            dayOfMonth: sanitizeDayOfMonth(state.dayOfMonth + 1)
        });
    }, [
        setState,
        state
    ]);
    const onDecrementDayOfMonthSpinButton = useCallback(()=>{
        setState({
            ...state,
            dayOfMonth: sanitizeDayOfMonth(state.dayOfMonth - 1)
        });
    }, [
        setState,
        state
    ]);
    const onValidateDayOfMonthSpinButton = useCallback((value)=>{
        // Try to convert the input string to a number
        setState({
            ...state,
            dayOfMonth: sanitizeDayOfMonth(parseInt(value, 10))
        });
    }, [
        setState,
        state
    ]);
    const onChangeMonthDropdown = useCallback((evt, option)=>setState({
            ...state,
            month: option.key
        }), [
        setState,
        state
    ]);
    const dayOfMonthSpinButton = /*#__PURE__*/ React.createElement(SpinButton, {
        className: "spinButton",
        key: "dayOfMonthSpinButton",
        value: dayOfMonth.toString(),
        styles: getSpinButtonStyles(theme, spinButtonDisabled),
        onIncrement: onIncrementDayOfMonthSpinButton,
        onDecrement: onDecrementDayOfMonthSpinButton,
        onValidate: onValidateDayOfMonthSpinButton,
        disabled: spinButtonDisabled,
        inputProps: {
            "aria-label": Strings.TaskRecurrence_DayOfMonthInputAriaLabel
        },
        incrementButtonAriaLabel: Strings.TaskRecurrence_IncrementDayOfMonthButtonAriaLabel,
        decrementButtonAriaLabel: Strings.TaskRecurrence_DecrementDayOfMonthButtonAriaLabel,
        min: MinDayOfMonth,
        max: MaxDayOfMonth,
        upArrowButtonStyles: getSpinButtonArrowStyles(theme, spinButtonDisabled || dayOfMonth === MaxDayOfMonth),
        downArrowButtonStyles: getSpinButtonArrowStyles(theme, spinButtonDisabled || dayOfMonth === MinDayOfMonth)
    });
    const monthDropdown = /*#__PURE__*/ React.createElement(Dropdown, {
        className: "monthDropdown",
        key: "absoluteMonthDropdown",
        options: useMemo(()=>getMonthDropdownOptions(), []),
        styles: getDropdownStyles(theme, isAbsoluteDisabled),
        selectedKey: month,
        onChange: onChangeMonthDropdown,
        disabled: isAbsoluteDisabled,
        ariaLabel: Strings.TaskRecurrence_MonthDropdownAriaLabel
    });
    // Construct the element order of the row based on the locale
    const absoluteRowElements = [];
    let stringsAndElements = [];
    let ariaLabel = "";
    if (isMonthly) {
        stringsAndElements = [
            Strings.TaskRecurrence_AbsoluteMonthlyPickerRow
        ];
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{0}", dayOfMonthSpinButton);
        ariaLabel = StringHelper.format(Strings.TaskRecurrence_AbsoluteMonthlyPickerRow, dayOfMonth.toString());
    } else if (isYearly) {
        stringsAndElements = [
            Strings.TaskRecurrence_AbsoluteYearlyPickerRow
        ];
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{0}", monthDropdown);
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{1}", dayOfMonthSpinButton);
        ariaLabel = StringHelper.format(Strings.TaskRecurrence_AbsoluteYearlyPickerRow, moment.months(getMomentMonthIndexFromRecurrenceMonth(month)), dayOfMonth.toString());
    }
    // Convert any remaining strings into span elements
    stringsAndElements.forEach((value, index)=>{
        if (typeof value === "string") {
            const stringToRender = value.trim();
            stringToRender !== "" && absoluteRowElements.push(/*#__PURE__*/ React.createElement("span", {
                className: "rowText",
                key: "absoluteRowText-" + index.toString()
            }, stringToRender));
        } else {
            absoluteRowElements.push(value);
        }
    });
    const setAbsolutePatternTypeFromRadioButton = useCallback(()=>setState({
            ...state,
            isAbsolute: true
        }), [
        setState,
        state
    ]);
    const getChoiceGroupOptionStylesWithTheme = useCallback((styleProps)=>getChoiceGroupOptionStyles(styleProps, theme), [
        theme
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "absoluteRow"
    }, /*#__PURE__*/ React.createElement(ChoiceGroupOption, {
        className: "inputButton",
        itemKey: "absolute",
        key: "absolute",
        id: "absolute",
        name: "repeatChoiceGroup",
        text: "",
        styles: getChoiceGroupOptionStylesWithTheme,
        checked: isAbsolute,
        onChange: setAbsolutePatternTypeFromRadioButton,
        tabIndex: 0,
        "data-is-focusable": true,
        ariaLabel: ariaLabel,
        disabled: !validateRecurrenceParametersState(state)
    }), /*#__PURE__*/ React.createElement("div", {
        className: "absoluteRowElements"
    }, absoluteRowElements));
}
/**
 * Relative recurrence fields row
 */ function RelativeRow() {
    const { state, setState } = useNullSafeContext(ComponentContext);
    const { daysOfWeek, isAbsolute, month, recurrenceDropdownType, weekIndex } = state;
    const theme = useTheme();
    const isMonthly = recurrenceDropdownType === RecurrenceDropdownType.Monthly;
    const isYearly = recurrenceDropdownType === RecurrenceDropdownType.Yearly;
    const isRelativeDisabled = isAbsolute || !validateRecurrenceParametersState(state);
    const onChangeWeekIndexDropdown = useCallback((evt, option)=>{
        const weekIndex = option.key;
        setState({
            ...state,
            weekIndex
        });
    }, [
        setState,
        state
    ]);
    const onChangeDayOfWeekDropdown = useCallback((evt, option)=>{
        const dayOfWeek = option.key;
        setState({
            ...state,
            daysOfWeek: [
                dayOfWeek
            ]
        });
    }, [
        setState,
        state
    ]);
    const onChangeMonthDropdown = useCallback((evt, option)=>setState({
            ...state,
            month: option.key
        }), [
        setState,
        state
    ]);
    const weekIndexDropdown = /*#__PURE__*/ React.createElement(Dropdown, {
        className: "weekIndexDropdown",
        key: "weekIndexDropdown",
        options: useMemo(()=>getWeekIndexDropdownOptions(), []),
        styles: getDropdownStyles(theme, isRelativeDisabled),
        selectedKey: weekIndex,
        onChange: onChangeWeekIndexDropdown,
        disabled: isRelativeDisabled,
        ariaLabel: Strings.TaskRecurrence_WeekIndexDropdownAriaLabel
    });
    const dayOfWeekDropdown = /*#__PURE__*/ React.createElement(Dropdown, {
        className: "dayOfWeekDropdown",
        key: "dayOfWeekDropdown",
        options: useMemo(()=>getDaysOfWeekDropdownOptions(), []),
        styles: getDropdownStyles(theme, isRelativeDisabled),
        selectedKey: daysOfWeek[0],
        onChange: onChangeDayOfWeekDropdown,
        disabled: isRelativeDisabled,
        ariaLabel: Strings.TaskRecurrence_DayOfWeekDropdownAriaLabel
    });
    const monthDropdown = /*#__PURE__*/ React.createElement(Dropdown, {
        className: "monthDropdown",
        key: "relativeMonthDropdown",
        options: useMemo(()=>getMonthDropdownOptions(), []),
        styles: getDropdownStyles(theme, isRelativeDisabled),
        selectedKey: month,
        onChange: onChangeMonthDropdown,
        disabled: isRelativeDisabled,
        ariaLabel: Strings.TaskRecurrence_MonthDropdownAriaLabel
    });
    // Construct the element order and aria label of the row based on the locale
    const relativeRowElements = [];
    let stringsAndElements = [];
    let ariaLabel = "";
    const weekday = daysOfWeek[0];
    if (isMonthly) {
        stringsAndElements = [
            Strings.TaskRecurrence_RelativeMonthlyPickerRow
        ];
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{0}", weekIndexDropdown);
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{1}", dayOfWeekDropdown);
        ariaLabel = StringHelper.format(Strings.TaskRecurrence_RelativeMonthlyPickerRow, getOrdinalNumberText(weekIndex), moment.weekdays(getMomentDayIndexFromDayOfWeek(weekday)));
    } else if (isYearly) {
        stringsAndElements = [
            Strings.TaskRecurrence_RelativeYearlyPickerRow
        ];
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{0}", weekIndexDropdown);
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{1}", dayOfWeekDropdown);
        stringsAndElements = insertElementOrStringIntoTextArray(stringsAndElements, "{2}", monthDropdown);
        ariaLabel = StringHelper.format(Strings.TaskRecurrence_RelativeYearlyPickerRow, getOrdinalNumberText(weekIndex), moment.weekdays(getMomentDayIndexFromDayOfWeek(weekday)), moment.months(getMomentMonthIndexFromRecurrenceMonth(month)));
    }
    // Convert any remaining strings into span elements
    stringsAndElements.forEach((value, index)=>{
        if (typeof value === "string") {
            const stringToRender = value.trim();
            stringToRender !== "" && relativeRowElements.push(/*#__PURE__*/ React.createElement("span", {
                className: "rowText",
                key: "relativeRowText-" + index.toString()
            }, stringToRender));
        } else {
            relativeRowElements.push(value);
        }
    });
    const setRelativePatternTypeFromRadioButton = useCallback(()=>{
        // When switching to relative mode, ensure daysOfWeek has exactly one day
        const newDaysOfWeek = state.daysOfWeek.length === 1 ? state.daysOfWeek : [
            state.daysOfWeek[0]
        ];
        setState({
            ...state,
            isAbsolute: false,
            daysOfWeek: newDaysOfWeek
        });
    }, [
        setState,
        state
    ]);
    const getChoiceGroupOptionStylesWithTheme = useCallback((styleProps)=>getChoiceGroupOptionStyles(styleProps, theme), [
        theme
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "relativeRow"
    }, /*#__PURE__*/ React.createElement(ChoiceGroupOption, {
        className: "inputButton",
        itemKey: "relative",
        key: "relative",
        id: "relative",
        name: "repeatChoiceGroup",
        text: "",
        styles: getChoiceGroupOptionStylesWithTheme,
        checked: !isAbsolute,
        onChange: setRelativePatternTypeFromRadioButton,
        tabIndex: 0,
        "data-is-focusable": true,
        ariaLabel: ariaLabel,
        disabled: !validateRecurrenceParametersState(state)
    }), /*#__PURE__*/ React.createElement("div", {
        className: "relativeRowElements"
    }, relativeRowElements));
}
/**
 * Get month dropdown options
 */ function getMonthDropdownOptions() {
    const options = [];
    for(let i = 1; i < 13; i++){
        // Recurrence month is 1-based indexing, moment month is 0-based indexing
        options.push({
            key: i,
            text: moment.months(i - 1)
        });
    }
    return options;
}
/**
 * Get days of week dropdown options
 */ function getDaysOfWeekDropdownOptions() {
    const daysOfWeek = getDaysOfWeekByStartDay(moment.localeData().firstDayOfWeek());
    return map(daysOfWeek, (day)=>{
        return {
            key: day,
            text: moment.weekdays(getMomentDayIndexFromDayOfWeek(day))
        };
    });
}
/**
 * Get week index dropdown options
 */ function getWeekIndexDropdownOptions() {
    return [
        {
            key: RelativeWeekIndex.First,
            text: Strings.TaskRecurrence_FirstWeekDropdownOptionText
        },
        {
            key: RelativeWeekIndex.Second,
            text: Strings.TaskRecurrence_SecondWeekDropdownOptionText
        },
        {
            key: RelativeWeekIndex.Third,
            text: Strings.TaskRecurrence_ThirdWeekDropdownOptionText
        },
        {
            key: RelativeWeekIndex.Fourth,
            text: Strings.TaskRecurrence_FourthWeekDropdownOptionText
        },
        {
            key: RelativeWeekIndex.Last,
            text: Strings.TaskRecurrence_LastWeekDropdownOptionText
        }
    ];
}
/**
 * Get recurrence dropdown type options
 */ function getRecurrenceDropdownTypeOptions() {
    return [
        {
            key: RecurrenceDropdownType.Daily,
            text: Strings.TaskRecurrence_DayRepeatOptionText
        },
        {
            key: RecurrenceDropdownType.Weekly,
            text: Strings.TaskRecurrence_WeekRepeatOptionText
        },
        {
            key: RecurrenceDropdownType.Monthly,
            text: Strings.TaskRecurrence_MonthRepeatOptionText
        },
        {
            key: RecurrenceDropdownType.Yearly,
            text: Strings.TaskRecurrence_YearRepeatOptionText
        }
    ];
}
/**
 * Get default state of the dialog
 */ function getDefaultState(props) {
    // Get current task recurrence pattern or build default pattern which is equivalent to daily
    const pattern = props.task.recurrence?.schedule?.pattern ?? RecurrencePattern.builder.build();
    const isAbsoluteType = pattern.recurrencePatternType !== RecurrencePatternType.RelativeMonthly && pattern.recurrencePatternType !== RecurrencePatternType.RelativeYearly;
    // Use task dueDate if possible, otherwise use task startDate, lastly use moment
    const dueDate = props.task.dueDate ?? props.task.startDate ?? ToUTC10am(moment());
    // Default day of month based on dueDate
    const defaultDayOfMonth = dueDate.date();
    // Default day of week based on dueDate
    const defaultDayOfWeek = getDayOfWeekFromMomentIndex(dueDate.day());
    // Default week index based on dueDate
    const defaultWeekIndex = getWeekIndexFromMoment(dueDate);
    // Default month based on dueDate
    const defaultMonth = getRecurrenceMonthIndexFromMoment(dueDate);
    return {
        dayOfMonth: pattern.dayOfMonth ?? defaultDayOfMonth,
        daysOfWeek: pattern.daysOfWeek ?? [
            defaultDayOfWeek
        ],
        interval: pattern.interval,
        isAbsolute: isAbsoluteType,
        month: pattern.month ?? defaultMonth,
        recurrenceDropdownType: getRepeatPatternDropdownKey(pattern.recurrencePatternType),
        weekIndex: pattern.weekIndex ?? defaultWeekIndex
    };
}
/**
 * Save button click callback
 */ function onClickSaveButton(props, context) {
    const { onDismiss, onUpdateTask, task: currentTask } = props;
    const { state, engagementLogger } = context;
    const { dayOfMonth, daysOfWeek, interval, isAbsolute, month, recurrenceDropdownType, weekIndex } = state;
    if (!validateRecurrenceParametersState(state)) {
        return;
    }
    engagementLogger.logEngagement("CustomRecurrenceDialog", EngagementTelemetryConstants.RecurrenceOptionSelected);
    let recurrencePattern;
    switch(recurrenceDropdownType){
        case RecurrenceDropdownType.Weekly:
            // All 7 days selected is equivalent to daily recurrence
            if (daysOfWeek.length === 7) {
                recurrencePattern = RecurrencePattern.builder.withDaily(interval).build();
            } else {
                recurrencePattern = RecurrencePattern.builder.withWeekly(daysOfWeek, interval).build();
            }
            break;
        case RecurrenceDropdownType.Monthly:
            if (isAbsolute) {
                recurrencePattern = RecurrencePattern.builder.withAbsoluteMonthly(dayOfMonth, interval).build();
            } else {
                recurrencePattern = RecurrencePattern.builder.withRelativeMonthly(daysOfWeek[0], weekIndex, interval).build();
            }
            break;
        case RecurrenceDropdownType.Yearly:
            if (isAbsolute) {
                recurrencePattern = RecurrencePattern.builder.withAbsoluteYearly(dayOfMonth, month, interval).build();
            } else {
                recurrencePattern = RecurrencePattern.builder.withRelativeYearly(daysOfWeek[0], month, weekIndex, interval).build();
            }
            break;
        case RecurrenceDropdownType.Daily:
        default:
            recurrencePattern = RecurrencePattern.builder.withDaily(interval).build();
            break;
    }
    let schedule;
    if (currentTask.recurrence?.schedule == null) {
        // Recurrence creation or recreation, build out schedule with pattern defined above
        schedule = RecurrenceSchedule.builder.withRecurrencePattern(recurrencePattern).build();
    } else {
        // Recurrence schedule exists, update schedule with pattern defined above
        schedule = currentTask.recurrence.schedule.setRecurrencePattern(recurrencePattern);
    }
    const updatedTask = currentTask.setRecurrenceSchedule(schedule);
    onUpdateTask(currentTask, updatedTask);
    onDismiss();
}
/**
 * Get resulting state update based on the new recurrence dropdown type
 * @param patternType The recurrence dropdown type
 */ function getStateUpdateForNewRecurrenceType(newRecurrenceDropdownType, currentState, defaultDaysOfWeek) {
    if (newRecurrenceDropdownType !== currentState.recurrenceDropdownType) {
        // For views that render both absolute and relative pattern types (monthly & yearly)
        // validate & fill the non-selected pattern type so that the shown fields render non-blank values
        switch(newRecurrenceDropdownType){
            case RecurrenceDropdownType.Weekly:
                return {
                    interval: sanitizeInterval(currentState.interval, getRecurrenceIntervalLimits(RecurrenceDropdownType.Weekly)),
                    recurrenceDropdownType: RecurrenceDropdownType.Weekly
                };
            case RecurrenceDropdownType.Monthly:
                return {
                    interval: sanitizeInterval(currentState.interval, getRecurrenceIntervalLimits(RecurrenceDropdownType.Monthly)),
                    daysOfWeek: currentState.daysOfWeek?.length !== 1 ? defaultDaysOfWeek : currentState.daysOfWeek,
                    isAbsolute: true,
                    recurrenceDropdownType: RecurrenceDropdownType.Monthly
                };
            case RecurrenceDropdownType.Yearly:
                return {
                    interval: sanitizeInterval(currentState.interval, getRecurrenceIntervalLimits(RecurrenceDropdownType.Yearly)),
                    daysOfWeek: currentState.daysOfWeek?.length !== 1 ? defaultDaysOfWeek : currentState.daysOfWeek,
                    isAbsolute: true,
                    recurrenceDropdownType: RecurrenceDropdownType.Yearly
                };
            case RecurrenceDropdownType.Daily:
            default:
                return {
                    recurrenceDropdownType: RecurrenceDropdownType.Daily
                };
        }
    }
    return {};
}
/**
 * Validate the current recurrence settings.
 */ function validateRecurrenceParametersState(state) {
    const { dayOfMonth, daysOfWeek, interval, isAbsolute, month, recurrenceDropdownType } = state;
    let isValid = true;
    switch(recurrenceDropdownType){
        case RecurrenceDropdownType.Yearly:
            if (isAbsolute) {
                isValid = validateDayOfMonth(dayOfMonth) && validateMonth(month);
            } else {
                isValid = validateDaysOfWeekAsRelative(daysOfWeek) && validateMonth(month);
            }
            break;
        case RecurrenceDropdownType.Monthly:
            if (isAbsolute) {
                isValid = validateDayOfMonth(dayOfMonth);
            } else {
                isValid = validateDaysOfWeekAsRelative(daysOfWeek);
            }
            break;
        case RecurrenceDropdownType.Weekly:
            isValid = validateDaysOfWeekAsWeekly(daysOfWeek, interval);
            break;
        case RecurrenceDropdownType.Daily:
        default:
            break;
    }
    return isValid;
}
/**
 * Sanitize interval value
 * @param interval Interval value
 */ function sanitizeInterval(interval, maximum) {
    return isNaN(interval) ? MinRecurrenceInterval : clamp(interval, MinRecurrenceInterval, maximum);
}
/**
 * Sanitize dayOfMonth value
 * @param dayOfMonth Day of month
 */ function sanitizeDayOfMonth(dayOfMonth) {
    return isNaN(dayOfMonth) ? MinDayOfMonth : clamp(dayOfMonth, MinDayOfMonth, MaxDayOfMonth);
}
/**
 * Validate daysOfWeek fields with weekly pattern
 * @param daysOfWeek
 * @param interval
 */ function validateDaysOfWeekAsWeekly(daysOfWeek, interval) {
    return !isEmpty(daysOfWeek) && (daysOfWeek.length === 1 || interval === 1);
}
/**
 * Validate daysOfWeek fields with relative monthly/yearly pattern
 * @param daysOfWeek
 */ function validateDaysOfWeekAsRelative(daysOfWeek) {
    return !isEmpty(daysOfWeek) && daysOfWeek.length === 1;
}
/**
 * Validate month fields
 * @param month
 */ function validateMonth(month) {
    return month >= MinMonth && month <= MaxMonth;
}
/**
 * Validate dayOfMonth fields
 * @param dayOfMonth
 */ function validateDayOfMonth(dayOfMonth) {
    return dayOfMonth >= MinDayOfMonth && dayOfMonth <= MaxDayOfMonth;
}
/**
 * Helper to generate the summary text prefix string
 */ function generateRecurrenceSummaryText(state) {
    const { dayOfMonth, interval, isAbsolute, month, recurrenceDropdownType, weekIndex } = state;
    // First, construct the prefix (text about the interval, e.g. "every month")
    let patternText = "";
    switch(recurrenceDropdownType){
        case RecurrenceDropdownType.Daily:
            if (interval === 1) {
                patternText = Strings.TaskRecurrence_DailyPatternTextSingular;
            } else {
                patternText = StringHelper.format(Strings.TaskRecurrence_DailyPatternTextPlural, interval.toString());
            }
            break;
        case RecurrenceDropdownType.Weekly:
            if (interval === 1) {
                patternText = StringHelper.format(Strings.TaskRecurrence_WeeklyPatternTextSingular, generateDaysOfWeekText(state));
            } else {
                patternText = StringHelper.format(Strings.TaskRecurrence_WeeklyPatternTextPlural, interval.toString(), generateDaysOfWeekText(state));
            }
            break;
        case RecurrenceDropdownType.Monthly:
            if (isAbsolute) {
                if (interval === 1) {
                    patternText = StringHelper.format(Strings.TaskRecurrence_AbsoluteMonthlyPatternTextSingular, dayOfMonth.toString());
                } else {
                    patternText = StringHelper.format(Strings.TaskRecurrence_AbsoluteMonthlyPatternTextPlural, interval.toString(), dayOfMonth.toString());
                }
            } else {
                if (interval === 1) {
                    patternText = StringHelper.format(Strings.TaskRecurrence_RelativeMonthlyPatternTextSingular, getOrdinalNumberText(weekIndex), generateDaysOfWeekText(state));
                } else {
                    patternText = StringHelper.format(Strings.TaskRecurrence_RelativeMonthlyPatternTextPlural, interval.toString(), getOrdinalNumberText(weekIndex), generateDaysOfWeekText(state));
                }
            }
            break;
        case RecurrenceDropdownType.Yearly:
            if (isAbsolute) {
                if (interval === 1) {
                    patternText = StringHelper.format(Strings.TaskRecurrence_AbsoluteYearlyPatternTextSingular, dayOfMonth.toString(), moment.months(getMomentMonthIndexFromRecurrenceMonth(month)));
                } else {
                    patternText = StringHelper.format(Strings.TaskRecurrence_AbsoluteYearlyPatternTextPlural, interval.toString(), dayOfMonth.toString(), moment.months(getMomentMonthIndexFromRecurrenceMonth(month)));
                }
            } else {
                if (interval === 1) {
                    patternText = StringHelper.format(Strings.TaskRecurrence_RelativeYearlyPatternTextSingular, getOrdinalNumberText(weekIndex), generateDaysOfWeekText(state), moment.months(getMomentMonthIndexFromRecurrenceMonth(month)));
                } else {
                    patternText = StringHelper.format(Strings.TaskRecurrence_RelativeYearlyPatternTextPlural, interval.toString(), getOrdinalNumberText(weekIndex), generateDaysOfWeekText(state), moment.months(getMomentMonthIndexFromRecurrenceMonth(month)));
                }
            }
            break;
        default:
            break;
    }
    return StringHelper.format(Strings.TaskRecurrence_RecurrenceSummaryText, patternText);
}
/**
 * Helper to generate the summary text prefix string
 */ function generateDaysOfWeekText(state) {
    const { daysOfWeek } = state;
    // Next, construct the suffix (details about the pattern, e.g. "on day 1" or "on the Second Tuesday of August")
    let daysOfWeekText = "";
    const dayIndexes = daysOfWeek.map((day)=>{
        return getMomentDayIndexFromDayOfWeek(day);
    }).sort();
    if (dayIndexes.length === 1) {
        // For a single selected weekday, the pattern detail text is the weekday itself
        daysOfWeekText = moment.weekdays(dayIndexes[0]);
    } else if (dayIndexes.length === 2) {
        // For a two selected weekdays, use the two-value specific weekday text
        daysOfWeekText = StringHelper.format(Strings.TaskRecurrence_WeekdaySpecifierForTwoValues, moment.weekdays(dayIndexes[0]), moment.weekdays(dayIndexes[1]));
    } else if (dayIndexes.length > 2) {
        // For a more than 2 selected weekdays, append each weekday to string, with weekday list separator in-between
        forEach(dayIndexes, (dayIndex, iter)=>{
            daysOfWeekText += moment.weekdays(dayIndex);
            if (iter !== dayIndexes.length - 1) {
                daysOfWeekText += Strings.TaskRecurrence_WeekdaySpecifierListSeparator;
            }
        });
    }
    return daysOfWeekText;
}
/**
 * Helper to create element/string array, consisting of element injected within a string
 * @param orderingStrings The array of elements and strings into which the element should be injected
 * @param textSeparator The string separator to find the location to inject the element
 * @param elementOrString The element or string to inject
 */ function insertElementOrStringIntoTextArray(orderingStrings, textSeparator, elementOrString) {
    const elements = clone(orderingStrings);
    orderingStrings.forEach((orderingString, index)=>{
        if (typeof orderingString === "string") {
            const splitStrings = orderingString.split(textSeparator);
            if (splitStrings.length === 2) {
                elements.splice(index, 1, splitStrings[0], elementOrString, splitStrings[1]);
            }
        }
    });
    return elements;
}
/**
 * Helper to get ordinal number text from weekIndex
 * @param weekIndex The weekIndex to get the ordinal number text for
 */ export function getOrdinalNumberText(weekIndex) {
    switch(weekIndex){
        case RelativeWeekIndex.First:
            return Strings.TaskRecurrence_FirstWeekOrdinalNumberText;
        case RelativeWeekIndex.Second:
            return Strings.TaskRecurrence_SecondWeekOrdinalNumberText;
        case RelativeWeekIndex.Third:
            return Strings.TaskRecurrence_ThirdWeekOrdinalNumberText;
        case RelativeWeekIndex.Fourth:
            return Strings.TaskRecurrence_FourthWeekOrdinalNumberText;
        case RelativeWeekIndex.Last:
        default:
            return Strings.TaskRecurrence_LastWeekOrdinalNumberText;
    }
}
/**
 * Gets dropdown key for the current recurrence pattern
 * @param recurrencePatternType The recurrence pattern to convert into repeat pattern dropdown key
 */ export function getRepeatPatternDropdownKey(pattern) {
    switch(pattern){
        case RecurrencePatternType.Weekly:
            return RecurrenceDropdownType.Weekly;
        case RecurrencePatternType.AbsoluteMonthly:
        case RecurrencePatternType.RelativeMonthly:
            return RecurrenceDropdownType.Monthly;
        case RecurrencePatternType.AbsoluteYearly:
        case RecurrencePatternType.RelativeYearly:
            return RecurrenceDropdownType.Yearly;
        case RecurrencePatternType.Daily:
        default:
            return RecurrenceDropdownType.Daily;
    }
}
